import { useCallback, useMemo } from 'react';
import type {
  ConditionalNormalization,
  FieldSuggestion,
  NormalizationConfig,
  NormalizationProfile,
} from '../../types/pipelineConfig';
import { getDefaultNormalizationConfig, getFieldSuggestions } from '../../utils/pipelineDefaults';
import { StepConfigHeader } from './StepConfigHeader';
import { ConditionEditor } from './normalization/ConditionEditor';
import { NormalizationProfileEditor } from './normalization/NormalizationProfileEditor';

interface NormalizationConfigEditorProps {
  config?: NormalizationConfig;
  onChange: (config: NormalizationConfig) => void;
  endpointType: string | number;
  disabled?: boolean;
  tenantId?: string;
  isSystemDefault?: boolean;
  hideToggle?: boolean;
}

function createConditionalId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `conditional-${crypto.randomUUID()}`;
  }
  return `conditional-${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`;
}

function deriveDefaultProfile(config: NormalizationConfig): NormalizationProfile {
  return {
    name: config.defaultNormalization?.name ?? 'Default Normalization Profile',
    rules: config.defaultNormalization?.rules ?? [],
    severityMapping: config.defaultNormalization?.severityMapping,
    priorityMapping: config.defaultNormalization?.priorityMapping,
    priorityMappingConfig: config.defaultNormalization?.priorityMappingConfig,
    defaults: config.defaultNormalization?.defaults,
    titleTemplate: config.defaultNormalization?.titleTemplate,
    descriptionTemplate: config.defaultNormalization?.descriptionTemplate,
  };
}

function normalizeOrders(items: ConditionalNormalization[]): ConditionalNormalization[] {
  return items.map((item, index) => ({ ...item, order: index + 1 }));
}

export function NormalizationConfigEditor({
  config: rawConfig,
  onChange,
  endpointType,
  disabled = false,
  tenantId,
  isSystemDefault = false,
  hideToggle,
}: NormalizationConfigEditorProps) {
  const defaultConfig = useMemo(() => getDefaultNormalizationConfig(endpointType), [endpointType]);
  const config = useMemo<NormalizationConfig>(() => ({
    ...defaultConfig,
    ...(rawConfig ?? {}),
    defaultNormalization: {
      ...defaultConfig.defaultNormalization,
      ...(rawConfig?.defaultNormalization ?? {}),
      name:
        rawConfig?.defaultNormalization?.name ??
        defaultConfig.defaultNormalization?.name ??
        'Default Normalization Profile',
      rules:
        rawConfig?.defaultNormalization?.rules ??
        defaultConfig.defaultNormalization?.rules ??
        [],
    },
    conditionalNormalizations:
      rawConfig?.conditionalNormalizations ??
      defaultConfig.conditionalNormalizations ??
      [],
  }), [defaultConfig, rawConfig]);
  const isStepEnabled = (config as NormalizationConfig & { isEnabled?: boolean }).isEnabled ?? true;
  const fieldSuggestions = useMemo<FieldSuggestion[]>(
    () => getFieldSuggestions(endpointType, 'normalization'),
    [endpointType],
  );

  const defaultProfile = useMemo(
    () => deriveDefaultProfile(config),
    [config],
  );

  const conditionalNormalizations = useMemo(
    () => normalizeOrders(config.conditionalNormalizations ?? []),
    [config.conditionalNormalizations],
  );

  const emitConfig = useCallback(
    (profile: NormalizationProfile, conditionals: ConditionalNormalization[]) => {
      const nextConfig: NormalizationConfig = {
        ...config,
        defaultNormalization: profile,
        conditionalNormalizations: normalizeOrders(conditionals),
      };
      onChange(nextConfig);
    },
    [config, onChange],
  );

  const updateDefaultProfile = useCallback(
    (profile: NormalizationProfile) => {
      emitConfig(profile, conditionalNormalizations);
    },
    [conditionalNormalizations, emitConfig],
  );

  const addConditional = useCallback(() => {
    const conditionalId = createConditionalId();
    const next: ConditionalNormalization[] = [
      ...conditionalNormalizations,
      {
        id: conditionalId,
        name: `Conditional Normalization ${conditionalNormalizations.length + 1}`,
        order: conditionalNormalizations.length + 1,
        isEnabled: true,
        conditions: [],
        conditionLogic: 'and',
        profile: {
          name: `Profile ${conditionalNormalizations.length + 1}`,
          rules: [],
          priorityMapping: {},
          defaults: {},
        },
      },
    ];
    emitConfig(defaultProfile, next);
  }, [conditionalNormalizations, defaultProfile, emitConfig]);

  const updateConditional = useCallback(
    (index: number, conditional: ConditionalNormalization) => {
      const next = conditionalNormalizations.map((item, itemIndex) =>
        itemIndex === index ? conditional : item,
      );
      emitConfig(defaultProfile, next);
    },
    [conditionalNormalizations, defaultProfile, emitConfig],
  );

  const removeConditional = useCallback(
    (index: number) => {
      const next = conditionalNormalizations.filter((_, itemIndex) => itemIndex !== index);
      emitConfig(defaultProfile, next);
    },
    [conditionalNormalizations, defaultProfile, emitConfig],
  );

  const moveConditional = useCallback(
    (index: number, direction: 'up' | 'down') => {
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= conditionalNormalizations.length) {
        return;
      }

      const next = [...conditionalNormalizations];
      const [item] = next.splice(index, 1);
      next.splice(targetIndex, 0, item);
      emitConfig(defaultProfile, next);
    },
    [conditionalNormalizations, defaultProfile, emitConfig],
  );

  return (
    <div className="space-y-6">
        <StepConfigHeader
          title="Normalization"
          isEnabled={isStepEnabled}
          onToggleEnabled={(enabled) =>
            onChange({ ...config, isEnabled: enabled } as NormalizationConfig & { isEnabled: boolean })
          }
          isSystemDefault={isSystemDefault}
          disabled={disabled}
          hideToggle={hideToggle}
        />
        <div className={isStepEnabled ? 'space-y-6' : 'space-y-6 opacity-50 pointer-events-none'}>
        <details className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800" aria-label="Default normalization profile">
          <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
            <span>📝 Default Normalization Profile</span>
            <span className="text-xs rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5">1</span>
          </summary>
          <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700">
            <div className="space-y-4">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              This profile is applied first. Legacy fields are mirrored from this profile for backward compatibility.
            </p>
            <NormalizationProfileEditor
              profile={defaultProfile}
              onProfileChange={updateDefaultProfile}
              fieldSuggestions={fieldSuggestions}
              showDefaults
              tenantId={tenantId}
            />
            </div>
          </div>
        </details>

        <details className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
            <span>🔀 Conditional Normalizations</span>
            <span className="text-xs rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5">{conditionalNormalizations.length}</span>
          </summary>
          <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700">
            <div className="space-y-4">
            {!disabled && (
              <button type="button" onClick={addConditional} className="btn-secondary text-sm">
                + Add Conditional Normalization
              </button>
            )}

            {conditionalNormalizations.length === 0 ? (
              <p className="rounded-md border border-dashed border-gray-300 dark:border-gray-600 p-4 text-sm text-gray-500 dark:text-gray-400 italic text-center">
                No conditional normalizations configured.
              </p>
            ) : (
              <div className="space-y-3">
                {conditionalNormalizations.map((conditional, index) => {
                  const conditionCount = conditional.conditions.length;
                  const profileName = conditional.profile.name || conditional.name || '(unnamed profile)';

                  return (
                    <details
                      key={conditional.id}
                      className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                    >
                      <summary className="cursor-pointer px-4 py-3 flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <span className="truncate">
                          📋 #{index + 1} {profileName}
                        </span>
                        <span className="text-xs rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5">
                          {conditionCount} {conditionCount === 1 ? 'condition' : 'conditions'}
                        </span>
                      </summary>
                      <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700">
                        <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            className="input text-sm font-medium"
                            value={conditional.name}
                            onChange={(event) => updateConditional(index, { ...conditional, name: event.target.value })}
                            placeholder="Conditional normalization name"
                          />
                          <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                            Order: {conditional.order}
                          </span>
                          <label className="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300">
                            <input
                              type="checkbox"
                              checked={conditional.isEnabled}
                              onChange={(event) => updateConditional(index, { ...conditional, isEnabled: event.target.checked })}
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            Enabled
                          </label>
                          <button
                            type="button"
                            onClick={() => moveConditional(index, 'up')}
                            disabled={index === 0}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40"
                            title="Move up"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            onClick={() => moveConditional(index, 'down')}
                            disabled={index >= conditionalNormalizations.length - 1}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40"
                            title="Move down"
                          >
                            ↓
                          </button>
                          <button
                            type="button"
                            onClick={() => removeConditional(index)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:text-red-600 disabled:opacity-40"
                            title="Delete conditional normalization"
                          >
                            ×
                          </button>
                        </div>

                        <ConditionEditor
                          conditions={conditional.conditions}
                          conditionLogic={conditional.conditionLogic}
                          onConditionsChange={(conditions) => updateConditional(index, { ...conditional, conditions })}
                          onLogicChange={(conditionLogic) => updateConditional(index, { ...conditional, conditionLogic })}
                          fieldSuggestions={fieldSuggestions}
                        />

                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Profile</h4>
                          <NormalizationProfileEditor
                            profile={conditional.profile}
                            onProfileChange={(profile) => updateConditional(index, { ...conditional, profile })}
                            fieldSuggestions={fieldSuggestions}
                            showDefaults
                            tenantId={tenantId}
                            isConditionalProfile
                          />
                        </div>
                        </div>
                      </div>
                    </details>
                  );
                })}
              </div>
            )}
            </div>
          </div>
        </details>
        </div>
    </div>
  );
}
